import { Fragment, lazy, Suspense, useEffect, useRef, useState } from 'react';
import { LAYOUT_LIMITS, type Me, type WsEvent } from '@jerome2/shared';
import { connectWs, wsUrl } from '../lib/ws.ts';
import { useStore } from '../lib/store.ts';
import { PaneContext, useShortcuts } from '../lib/keys.ts';
import { useWidth } from '../lib/size.ts';
import { useVisualViewport } from '../lib/viewport.ts';
import { app, applySettings, NARROW_QUERY, noteServerVersion, saveSettings, setMe, WIDE_QUERY } from '../state/app.ts';
import { applyTaskEvent, loadActiveTasks, loadCounts } from '../state/tasks.ts';
import { applyMailEvent, loadFolders, markStale } from '../state/mail.ts';
import { applyChatEvent, chatStore, loadChats, loadMessages } from '../state/chat.ts';
import { applyStroomEvent, forgetStroom } from '../state/stroom.ts';
import { columnCrowded } from '../state/chatTabs.ts';
import { watchChatTurns } from '../state/chatTurns.ts';
import { fitWidth, pixelsText, useLayout, widthBounds } from '../state/layout.ts';
import { openTab, setActivePane, setPaneCount, TAB_TITLES, tabs, type Pane, type PaneId, type Tab } from '../state/tabs.ts';
import { errorToast, toast } from '../state/toast.ts';
import { TopBar } from './TopBar.tsx';
import { TabBar } from './TabBar.tsx';
import { ChatColumn } from './ChatColumn.tsx';
import { FlaggedColumn, FlaggedList } from './FlaggedColumn.tsx';
import { openMailTab } from '../state/mailTabs.ts';
import { WorkersRail, WorkersStrip } from './WorkersRail.tsx';
import { PhoneTopBar } from './PhoneTopBar.tsx';
import { BottomNav } from './BottomNav.tsx';
import { MoreSheet } from './MoreSheet.tsx';
import { Resizer } from '../ui/Resizer.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { Toasts } from '../ui/Toasts.tsx';
import { Empty, Spinner } from '../ui/primitives.tsx';
import { MailScreen } from '../mail/MailScreen.tsx';
import { openDeepLink, takeStoredDeepLink } from '../lib/deeplink.ts';
import { stopAnswerReader } from '../voice/answerReader.ts';
import { primeAudio } from '../voice/tts.ts';

// De schil: bovenbalk, de gesprekkenlijst als vaste kolom links (ChatColumn; de tabbalk begint rechts ernaast, ticket 28083),
// één of twee panelen met tabbladen, werkerskolom (vanaf 1280 px in- en uitklapbaar tot een strook
// van 44 px, vanuit de kop van de kolom of de knop in de bovenbalk; `settings.workersRail.visible`), meldingen, websocket en
// sneltoetsen. Schermen per tabbladsoort worden hier gekozen. De gesprekkenkolom en de panelen worden samen gemeten: een
// chatscherm krijgt die ruimte per paneel mee (`room`), zodat zijn grenzen voor het mailpaneel (824 en 1240 px) dezelfde
// blijven als toen de lijst in het chatscherm stond, ook als de kolom in- of uitklapt; is het actieve tabblad geen chat en is
// die ruimte samen kleiner dan 1240 px, dan wijkt de lijst tot de strook (`columnCrowded`). Op de telefoon (narrow): een korte
// bovenbalk (met de microfoon van de spraakmodus, zodat die ook in een open mail of gesprek bereikbaar
// is), één scherm zonder tabbladbalk, de onderste navigatie (weg zodra een mail of gesprek open staat)
// en werkers en Meer als vel. Tussen 900 en 1280 px (tablet, half scherm): de desktop
// met één paneel en de werkers in een vel, zodat lijst en lezer de breedte houden. Na een onderbroken
// verbinding worden taken, tellers, lijst, mappen en alle bekende gesprekken (ook die van agenda, regels en
// ontwikkelen) één keer opnieuw geladen.
// Instelbare breedtes op de desktop (ticket 28376, `settings.layout`, state/layout.ts): een greep op de rand van de
// gesprekkenkolom (in ChatColumn), tussen de twee panelen (de verdeling als deel van beide panelen voor links, 0,25 tot 0,75)
// en op de rand van de werkerskolom; het mailpaneel heeft zijn greep in het chatscherm. Met twee panelen krijgt elk chatscherm
// zijn deel van kolom en panelen samen als `room`, zodat de grenzen van 824 en 1240 px met de werkelijke ruimte blijven rekenen.
// Het actieve paneel (`activePane`) is het paneel van de laatste klik, aanraking (pointerdown) of focus. Met twee panelen in beeld
// krijgt elk scherm zijn paneel mee (`PaneContext`), zodat zijn sneltoetsen alleen in het actieve paneel gelden (lib/keys.ts);
// de sneltoets van de schil zelf (v) geldt altijd.
// Helemaal links staat de kolom Gevlagd (ticket 30961, shell/FlaggedColumn.tsx): alle gevlagde mails, een klik opent het tabblad
// van die mail (state/mailTabs.ts). Ze staat buiten het gemeten werkblad (`work`): ze neemt ruimte zoals de werkerskolom, en de
// grenzen van de gesprekkenkolom en het mailpaneel (824 en 1240 px) rekenen met wat er voor kolom en panelen overblijft (de grens
// van het mailpaneel ging daarvoor van 960 naar 824 px: gesprek 480 px en paneel 300 px, zodat het op 1440 px met de kolom open
// naast het gesprek blijft). Haar breedte houdt de rest (`lane`: de kolom samen met kolom en panelen) minstens 480 px, en op een
// chattabblad minstens 824 px (SIDE_MIN), zodat het mailpaneel daar naast het gesprek blijft passen. Op een
// tabblad zonder chat wijkt ze onder 1240 px voor de rest tot de strook, zoals de gesprekkenkolom, en op een chattabblad als zelfs
// haar minimum van 220 px minder dan 824 px overlaat (onder 1044 px voor `lane`). Op de telefoon is ze een vel
// vanuit de bovenbalk (knop Gevlagde mails, met het aantal).
// Werker per vraag en ongelezen antwoord (ticket 30961, deel 2, state/chatTurns.ts): de schil start de bewaking van de beurten; een
// gesprek waar Jerome mee bezig is, staat als rij in de werkerskolom (ook in het vel, dat na een klik op zo'n rij sluit), en een
// antwoord buiten beeld geeft een stip op het tabblad en bij de rij in de gesprekkenlijst.

const ChatScreen = lazy(() => import('../chat/ChatScreen.tsx').then((m) => ({ default: m.ChatScreen })));
const AgendaScreen = lazy(() => import('../agenda/AgendaScreen.tsx').then((m) => ({ default: m.AgendaScreen })));
const RulesScreen = lazy(() => import('../rules/RulesScreen.tsx').then((m) => ({ default: m.RulesScreen })));
const DevScreen = lazy(() => import('../dev/DevScreen.tsx').then((m) => ({ default: m.DevScreen })));
const SettingsScreen = lazy(() => import('../settings/SettingsScreen.tsx').then((m) => ({ default: m.SettingsScreen })));
const KnowledgeScreen = lazy(() => import('../settings/KnowledgeScreen.tsx').then((m) => ({ default: m.KnowledgeScreen })));
const UsageScreen = lazy(() => import('../settings/UsageScreen.tsx').then((m) => ({ default: m.UsageScreen })));
const BriefingScreen = lazy(() => import('../settings/BriefingScreen.tsx').then((m) => ({ default: m.BriefingScreen })));
const ReleasesScreen = lazy(() => import('../releases/ReleasesScreen.tsx').then((m) => ({ default: m.ReleasesScreen })));
const ExcelScreen = lazy(() => import('../excel/ExcelScreen.tsx').then((m) => ({ default: m.ExcelScreen })));
const WhatsAppScreen = lazy(() => import('../whatsapp/WhatsAppScreen.tsx').then((m) => ({ default: m.WhatsAppScreen })));

export function Shell(props: { me: Me }) {
  const railVisible = useStore(app, (s) => s.settings.workersRail.visible);
  const narrow = useStore(app, (s) => s.narrow);
  const wide = useStore(app, (s) => s.wide);
  const phoneDetail = useStore(app, (s) => s.phoneDetail);
  const state = useStore(tabs);
  const [workersOpen, setWorkersOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [flaggedOpen, setFlaggedOpen] = useState(false);
  const [voice, setVoice] = useState(false);
  const lane = useRef<HTMLDivElement>(null);
  const work = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  const panesBox = useRef<HTMLDivElement>(null);
  // Met een andere terugval bij een andere indeling meet hij opnieuw: het werkblad bestaat alleen op de desktop.
  // Gemeten: kolom en panelen samen (`work`), de hele rij met de werkerskolom (`row`) en beide panelen samen (`panesBox`).
  const laneWidth = useWidth(lane, narrow ? 0 : 1280);
  const workWidth = useWidth(work, narrow ? 0 : 1280);
  const rowWidth = useWidth(row, narrow ? 0 : 1280);
  const panesWidth = useWidth(panesBox, narrow ? 0 : 1280);
  const railLayout = useLayout('workersRail');
  const split = useLayout('paneSplit');
  useVisualViewport(narrow);

  useEffect(() => {
    setMe(props.me);
  }, [props.me]);

  useEffect(() => {
    const mqNarrow = window.matchMedia(NARROW_QUERY);
    const mqWide = window.matchMedia(WIDE_QUERY);
    const apply = () => app.patch({ narrow: mqNarrow.matches, wide: mqWide.matches });
    apply();
    mqNarrow.addEventListener('change', apply);
    mqWide.addEventListener('change', apply);
    return () => {
      mqNarrow.removeEventListener('change', apply);
      mqWide.removeEventListener('change', apply);
    };
  }, []);

  // Werker per vraag en ongelezen antwoord (ticket 30961, deel 2): het begin van elke beurt voor de werkerskolom, en de stip bij een
  // antwoord dat binnenkwam terwijl het gesprek niet in beeld was.
  useEffect(() => watchChatTurns(), []);

  // Twee panelen passen alleen op een breed scherm: wordt het smaller, dan gaan de tabbladen van rechts naar links.
  useEffect(() => {
    if (!wide && tabs.get().paneCount === 2) setPaneCount(1);
  }, [wide]);

  useEffect(() => {
    if (!openDeepLink(location.search)) openDeepLink(takeStoredDeepLink());
    void loadActiveTasks().catch((e) => errorToast(e, 'Taken niet geladen'));
    void loadCounts().catch(() => undefined);
    void loadFolders().catch(() => undefined);
    const client = connectWs({
      url: wsUrl(),
      onEvent: (ev: WsEvent) => {
        if (ev.type === 'hello') noteServerVersion(ev.version);
        else if (ev.type === 'settings') applySettings(ev.settings);
        else if (ev.type === 'error') toast(`Fout gemeld: ${ev.error.message.slice(0, 120)}`, { tone: 'warn' });
        // Een melding over WhatsApp (sinds 2.3.1): ontkoppeld, geblokkeerd of gestopt; de server stuurt haar één keer per voorval.
        else if (ev.type === 'whatsapp.alert') toast(`${ev.alert.title}. ${ev.alert.text}`, { tone: 'warn', ms: 20000, action: { label: 'Bekijk', run: () => void openTab('whatsapp', { single: true }) } });
        else if (ev.type === 'draft.learned') {
          const l = ev.learned;
          const via = l.via === 'outlook' ? ' (via Outlook)' : '';
          if (l.unchanged) toast(`Je verzond het concept van Jerome ongewijzigd${via}: niets nieuws te leren`, { tone: 'ok' });
          else if (!l.lessons.length) toast(`Jerome bekeek je aanpassingen${via}: inhoudelijk, geen nieuwe stijlregel`, { tone: 'info' });
          else toast(`Jerome heeft geleerd${via}: ${l.lessons[0]!.text}${l.lessons.length > 1 ? ` (+${l.lessons.length - 1})` : ''}`, { tone: 'ok', ms: 10000, action: { label: 'Bekijk', run: () => void openTab('knowledge', { single: true }) } });
        }
        applyTaskEvent(ev);
        applyMailEvent(ev);
        applyChatEvent(ev);
        applyStroomEvent(ev);
        window.dispatchEvent(new CustomEvent('jerome-ws', { detail: ev }));
      },
      onReconnect: () => {
        void loadActiveTasks().catch(() => undefined);
        void loadCounts().catch(() => undefined);
        // Wat tijdens de onderbreking in Outlook veranderde: de lijst (via stale) en de mappen één keer opnieuw, op de achtergrond.
        markStale();
        void loadFolders(true, { background: true }).catch(() => undefined);
        // Gesprekken: de gesprekken (en hun status) als de client er al kent, en de berichten van elk gesprek waarvan hij ze
        // had of dat in een tabblad open staat, ook onder de agenda, de regels en het ontwikkelen.
        const known = chatStore.get();
        if (known.loaded || Object.keys(known.chats).length) void loadChats().catch(() => undefined);
        const ids = new Set(Object.keys(known.messages));
        for (const t of Object.values(tabs.get().tabs)) {
          const chatId = t.state['chatId'];
          if (typeof chatId === 'string') ids.add(chatId);
        }
        for (const id of ids) void loadMessages(id).catch(() => undefined);
      },
      onStatus: (s) => {
        app.patch({ wsStatus: s });
        // Zonder websocket komt geen melding van de stroom door (2.3.2): peilen zoals zonder stroom, tot de volgende hello.
        if (s !== 'open') forgetStroom();
      },
    });
    return () => client.close();
  }, []);

  useShortcuts((e) => {
    if (e.key === 'v' && !voice) {
      stopAnswerReader();
      primeAudio();
      openTab('mail', { single: true });
      setVoice(true);
      return true;
    }
    return false;
  }, [voice]);

  // Binnen de klik (of toets) van Jeroen: zo mag iOS daarna de stem van Jerome afspelen zonder nieuwe tik. Leest
  // Jerome in de chat nog een antwoord voor, dan stopt dat eerst: de stilte zou anders zijn element kapen.
  const startVoice = () => {
    stopAnswerReader();
    primeAudio();
    openTab('mail', { single: true });
    setVoice((v) => !v);
  };

  if (narrow) {
    // Eén scherm: het actieve paneel (of links als dat leeg is), zonder tabbladbalk.
    const pane = state.panes[state.activePane].tabIds.length ? state.panes[state.activePane] : state.panes.left;
    const tab = pane.activeTabId ? state.tabs[pane.activeTabId] : undefined;
    const title = tab ? (tab.kind === 'excel' ? tab.title : TAB_TITLES[tab.kind]) : 'Jerome';
    return (
      <div className="h-app flex flex-col bg-paper">
        <PhoneTopBar title={title} onVoice={startVoice} voiceActive={voice} onFlagged={() => setFlaggedOpen(true)} flaggedOpen={flaggedOpen} onWorkers={() => setWorkersOpen(true)} workersOpen={workersOpen} onMore={() => setMoreOpen(true)} moreOpen={moreOpen} />
        <div className="flex-1 min-h-0 bg-surface">
          {tab ? <Screen tab={tab} voice={voice && tab.kind === 'mail'} onVoiceEnd={() => setVoice(false)} /> : <Empty title="Niets open" text="Kies een functie onderin." icon="mail" />}
        </div>
        {!phoneDetail && !voice ? <BottomNav active={tab?.kind ?? null} onSelect={(kind) => openTab(kind, { single: true })} onVoice={startVoice} voiceActive={voice} onMore={() => setMoreOpen(true)} moreOpen={moreOpen} /> : null}
        {workersOpen ? (
          <Sheet title="Werkers" height="full" onClose={() => setWorkersOpen(false)}>
            <WorkersRail embedded onOpened={() => setWorkersOpen(false)} />
          </Sheet>
        ) : null}
        {moreOpen ? <MoreSheet onClose={() => setMoreOpen(false)} /> : null}
        {flaggedOpen ? (
          <Sheet title="Gevlagd" height="full" onClose={() => setFlaggedOpen(false)}>
            <FlaggedList
              embedded
              markedId={null}
              onOpen={(mail) => {
                setFlaggedOpen(false);
                openMailTab(mail, 'left');
              }}
            />
          </Sheet>
        ) : null}
        <Toasts />
      </div>
    );
  }

  const toggleRail = () => void saveSettings({ workersRail: { visible: !railVisible, showAll: app.get().settings.workersRail.showAll } });
  const panes: Pane[] = state.paneCount === 2 && wide ? [state.panes.left, state.panes.right] : [state.panes.left];
  // Het actieve paneel dat in beeld is: met één paneel altijd links.
  const activePane: PaneId = panes.length === 2 ? state.activePane : 'left';
  // Het deel van beide panelen per paneel (de ingestelde verdeling, op drie decimalen); met één paneel alles.
  const shares = panes.length === 2 ? [split.value, Math.round((1 - split.value) * 1000) / 1000] : [1];
  // De ruimte van een chatscherm: zijn deel van kolom en panelen samen (met de verdeling half om half: de helft, zoals voorheen).
  const roomOf = (i: number) => Math.round(workWidth * shares[i]!);
  // Een tabblad zonder chat (Mail, Agenda) houdt zijn breedte: onder 1240 px voor kolom en panelen samen wijkt de lijst.
  const crowded = columnCrowded(state, activePane, workWidth);
  const railBounds = widthBounds('workersRail', rowWidth);
  const railWidth = fitWidth('workersRail', railLayout.value, rowWidth);

  return (
    <div className="h-full flex flex-col bg-paper">
      {/* Onder 1280 px staat de werkerskolom niet naast het werk maar in een vel; de knop in de balk opent dat. */}
      <TopBar railVisible={wide ? railVisible : workersOpen} onToggleRail={wide ? toggleRail : () => setWorkersOpen((o) => !o)} onVoice={startVoice} voiceActive={voice} />
      <div ref={row} className="flex-1 min-h-0 flex">
        <div ref={lane} className="flex-1 min-w-0 flex">
          <FlaggedColumn pane={activePane} space={laneWidth} />
          <div ref={work} className="flex-1 min-w-0 flex">
            <ChatColumn pane={activePane} crowded={crowded} space={workWidth} />
            <div ref={panesBox} className="flex-1 min-w-0 flex">
              {panes.map((pane, i) => (
                <Fragment key={pane.id}>
                  {i === 1 ? (
                    <Resizer
                      label="Verdeling panelen"
                      value={split.value * 100}
                      min={LAYOUT_LIMITS.paneSplit.min * 100}
                      max={LAYOUT_LIMITS.paneSplit.max * 100}
                      defaultValue={LAYOUT_LIMITS.paneSplit.default * 100}
                      side="left"
                      unit={panesWidth / 100}
                      valueText={(v) => `linkerpaneel ${Math.round(v)} procent`}
                      onChange={(v) => split.change(v / 100)}
                      onCommit={(v) => split.commit(v / 100)}
                    />
                  ) : null}
                  <div className={`min-w-0 flex flex-col ${panes.length === 2 && pane.id === 'left' ? 'border-r border-line' : ''}`} style={{ flex: `${shares[i]} 1 0%` }} onPointerDownCapture={() => setActivePane(pane.id)} onFocusCapture={() => setActivePane(pane.id)}>
                    <PaneContext.Provider value={panes.length === 2 ? pane.id : null}>
                      <TabBar pane={pane} tabs={state.tabs} active={state.activePane === pane.id && panes.length === 2} />
                      <div className="flex-1 min-h-0 bg-surface">
                        {pane.activeTabId && state.tabs[pane.activeTabId] ? (
                          <Screen tab={state.tabs[pane.activeTabId]!} voice={voice && state.tabs[pane.activeTabId]!.kind === 'mail'} onVoiceEnd={() => setVoice(false)} room={roomOf(i)} />
                        ) : (
                          <Empty title="Leeg paneel" text="Kies een functie in de bovenbalk of sleep een tabblad hierheen." icon="panes2" />
                        )}
                      </div>
                    </PaneContext.Provider>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>
        </div>
        {wide && railVisible ? <Resizer label="Breedte werkerskolom" value={railWidth} min={railBounds.min} max={railBounds.max} defaultValue={LAYOUT_LIMITS.workersRail.default} side="right" valueText={pixelsText} onChange={railLayout.change} onCommit={railLayout.commit} /> : null}
        {wide ? (railVisible ? <WorkersRail width={railWidth} onCollapse={toggleRail} /> : <WorkersStrip onExpand={toggleRail} />) : null}
      </div>
      {!wide && workersOpen ? (
        <Sheet title="Werkers" height="full" onClose={() => setWorkersOpen(false)}>
          <WorkersRail embedded onOpened={() => setWorkersOpen(false)} />
        </Sheet>
      ) : null}
      <Toasts />
    </div>
  );
}

/** `room`: op de desktop de ruimte van het paneel samen met (zijn deel van) de gesprekkenkolom; het chatscherm rekent daarmee. */
function Screen(props: { tab: Tab; voice: boolean; onVoiceEnd: () => void; room?: number }) {
  const fallback = <Spinner className="p-6" />;
  switch (props.tab.kind) {
    case 'mail':
      return <MailScreen tab={props.tab} voice={props.voice} onVoiceEnd={props.onVoiceEnd} />;
    case 'chat':
      return <Suspense fallback={fallback}><ChatScreen tab={props.tab} room={props.room} /></Suspense>;
    case 'agenda':
      return <Suspense fallback={fallback}><AgendaScreen tab={props.tab} /></Suspense>;
    case 'rules':
      return <Suspense fallback={fallback}><RulesScreen tab={props.tab} /></Suspense>;
    case 'dev':
      return <Suspense fallback={fallback}><DevScreen tab={props.tab} /></Suspense>;
    case 'settings':
      return <Suspense fallback={fallback}><SettingsScreen /></Suspense>;
    case 'knowledge':
      return <Suspense fallback={fallback}><KnowledgeScreen /></Suspense>;
    case 'usage':
      return <Suspense fallback={fallback}><UsageScreen /></Suspense>;
    case 'briefing':
      return <Suspense fallback={fallback}><BriefingScreen tab={props.tab} /></Suspense>;
    case 'releases':
      return <Suspense fallback={fallback}><ReleasesScreen /></Suspense>;
    case 'excel':
      return <Suspense fallback={fallback}><ExcelScreen tab={props.tab} /></Suspense>;
    case 'whatsapp':
      return <Suspense fallback={fallback}><WhatsAppScreen /></Suspense>;
  }
}
